import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import apiClient from '../../../api/client';
import settings from '../../../utility/settings';

interface ILoginHeaderProps {
	isNewUser?: boolean;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
	if (isNewUser) {
		return (
			<>
				<div className='text-center h1 fw-bold mt-5'>Crea account</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>Benvenuto,</div>
			<div className='text-center h4 text-muted mb-5'>Accedi per continuare</div>
		</>
	);
};
LoginHeader.defaultProps = {
	isNewUser: false,
};

interface ILoginProps {
	isSignUp?: boolean;
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { setUser } = useContext(AuthContext);

	const { darkModeStatus } = useDarkMode();

	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);

	const navigate = useNavigate();
	const handleOnClick = useCallback(() => navigate('/'), [navigate]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginUsername?: string; loginPassword?: string } = {};

			if (!values.loginUsername) {
				errors.loginUsername = 'Obbligatorio';
			}

			if (!values.loginPassword) {
				errors.loginPassword = 'Obbligatorio';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			const result: any = await apiClient.post('/auth/logIn', {
				username: values.loginUsername,
				password: values.loginPassword,
				storeId: settings.storeId,
				admin: true,
			});

			if (result.ok && result.status === 200) {
				if (setUser) {
					setUser(result.data.token);
				}
				handleOnClick();
			} else if (result.data && result.status !== 200) {
				formik.setFieldError('loginPassword', result.data);
			} else {
				formik.setFieldError('loginPassword', 'Email o password errate.');
			}
		},
	});

	const formikRegister = useFormik({
		enableReinitialize: true,
		initialValues: {
			registerEmail: '',
			registerName: '',
			registerSurname: '',
			registerPassword: '',
			registerCellphone: '',
		},
		validate: (values) => {
			const errors: {
				registerEmail?: string;
				registerName?: string;
				registerSurname?: string;
				registerPassword?: string;
				registerCellphone?: string;
			} = {};

			if (!values.registerEmail) {
				errors.registerEmail = 'Obbligatorio';
			}

			if (!values.registerName) {
				errors.registerName = 'Obbligatorio';
			}
			if (!values.registerSurname) {
				errors.registerSurname = 'Obbligatorio';
			}
			if (!values.registerCellphone) {
				errors.registerCellphone = 'Obbligatorio';
			}
			if (!values.registerPassword) {
				errors.registerPassword = 'Obbligatorio';
			}

			return errors;
		},
		onSubmit: async (values) => {
			const result: any = await apiClient.post('/auth/register', {
				email: values.registerEmail,
				name: values.registerName,
				surname: values.registerSurname,
				password: values.registerPassword,
				cellphone: values.registerCellphone,
				storeId: settings.storeId,
				admin: true,
			});

			if (result.ok && result.data.status === 200) {
				alert('Grazie, il tuo account verrà convalidato presto.');
				handleOnClick();
				navigate('/');
			} else if (result.data) {
				formikRegister.setFieldError('registerPassword', result.data);
			} else {
				formikRegister.setFieldError('registerPassword', 'Email o password errate.');
			}
		},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const handleContinue = () => {
		setIsLoading(true);
		setTimeout(() => {
			setSignInPassword(true);

			setIsLoading(false);
		}, 1000);
	};

	return (
		<PageWrapper
			isProtected={false}
			title={singUpStatus ? 'Registrazione' : 'Login'}
			className={classNames({ 'bg-dark': !singUpStatus, 'bg-light': singUpStatus })}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-5'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}>
										<Logo width={200} />
									</Link>
								</div>
								<div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-dark': darkModeStatus,
									})}>
									<div className='row row-cols-2 g-3 pb-3 px-3 mt-0'>
										<div className='col'>
											<Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={singUpStatus}
												className='rounded-1 w-100 btn text-black'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												Login
											</Button>
										</div>
										<div className='col'>
											<Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={!singUpStatus}
												className='rounded-1 w-100 text-black'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												Registrazione
											</Button>
										</div>
									</div>
								</div>

								<LoginHeader isNewUser={singUpStatus} />

								<form className='row g-4'>
									{singUpStatus ? (
										<>
											<div className='col-12'>
												<FormGroup
													id='registerEmail'
													isFloating
													label='Email'>
													<Input
														type='email'
														autoComplete='email'
														value={formikRegister.values.registerEmail}
														isTouched={
															formikRegister.touched.registerEmail
														}
														invalidFeedback={
															formikRegister.errors.registerEmail
														}
														isValid={formikRegister.isValid}
														onChange={formikRegister.handleChange}
														onBlur={formikRegister.handleBlur}
														onFocus={() => {
															formikRegister.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='registerName'
													isFloating
													label='Nome'>
													<Input
														autoComplete='given-name'
														value={formikRegister.values.registerName}
														isTouched={
															formikRegister.touched.registerName
														}
														invalidFeedback={
															formikRegister.errors.registerName
														}
														isValid={formikRegister.isValid}
														onChange={formikRegister.handleChange}
														onBlur={formikRegister.handleBlur}
														onFocus={() => {
															formikRegister.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='registerSurname'
													isFloating
													label='Cognome'>
													<Input
														autoComplete='family-name'
														value={
															formikRegister.values.registerSurname
														}
														isTouched={
															formikRegister.touched.registerSurname
														}
														invalidFeedback={
															formikRegister.errors.registerSurname
														}
														isValid={formikRegister.isValid}
														onChange={formikRegister.handleChange}
														onBlur={formikRegister.handleBlur}
														onFocus={() => {
															formikRegister.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='registerCellphone'
													isFloating
													label='Cellulare'>
													<Input
														autoComplete='cellphone'
														value={
															formikRegister.values.registerCellphone
														}
														isTouched={
															formikRegister.touched.registerCellphone
														}
														invalidFeedback={
															formikRegister.errors.registerCellphone
														}
														isValid={formikRegister.isValid}
														onChange={formikRegister.handleChange}
														onBlur={formikRegister.handleBlur}
														onFocus={() => {
															formikRegister.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='registerPassword'
													isFloating
													label='Password'>
													<Input
														type='password'
														autoComplete='password'
														value={
															formikRegister.values.registerPassword
														}
														isTouched={
															formikRegister.touched.registerPassword
														}
														invalidFeedback={
															formikRegister.errors.registerPassword
														}
														isValid={formikRegister.isValid}
														onChange={formikRegister.handleChange}
														onBlur={formikRegister.handleBlur}
														onFocus={() => {
															formikRegister.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													color='info'
													className='w-100 py-3 text-black'
													onClick={formikRegister.handleSubmit}>
													Registrati
												</Button>
											</div>
										</>
									) : (
										<>
											<div className='col-12'>
												<FormGroup
													id='loginUsername'
													isFloating
													label='Username o email'
													className={classNames({
														'd-none': signInPassword,
													})}>
													<Input
														type='email'
														autoComplete='username'
														value={formik.values.loginUsername}
														isTouched={formik.touched.loginUsername}
														invalidFeedback={
															formik.errors.loginUsername
														}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>
												{signInPassword && (
													<div className='text-center h4 mb-3 fw-bold'>
														Ciao, {formik.values.loginUsername}.
													</div>
												)}
												<FormGroup
													id='loginPassword'
													isFloating
													label='Password'
													className={classNames({
														'd-none': !signInPassword,
													})}>
													<Input
														type='password'
														autoComplete='current-password'
														value={formik.values.loginPassword}
														isTouched={formik.touched.loginPassword}
														invalidFeedback={
															formik.errors.loginPassword
														}
														validFeedback='Ok!'
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												{!signInPassword ? (
													<Button
														color='warning'
														className='w-100 py-3'
														isDisable={!formik.values.loginUsername}
														onClick={handleContinue}>
														{isLoading && (
															<Spinner isSmall inButton isGrow />
														)}
														Continua
													</Button>
												) : (
													<Button
														color='warning'
														className='w-100 py-3'
														onClick={formik.handleSubmit}>
														Login
													</Button>
												)}
											</div>
										</>
									)}
								</form>
							</CardBody>
						</Card>
						<div className='text-center'>
							<a
								href='/'
								className={classNames('text-decoration-none me-3', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Privacy policy
							</a>
							<a
								href='/'
								className={classNames('link-light text-decoration-none', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Terms of use
							</a>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};
Login.propTypes = {
	isSignUp: PropTypes.bool,
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;
